<template>
  <div v-if="isOpen" class="fixed inset-0 z-[70] overflow-hidden" @keydown.esc="$emit('close')">

    <div class="absolute inset-0 bg-white w-full h-full flex flex-col">

      <!-- HEADER -->
      <div class="bg-gradient-to-r from-red-600 to-red-800 text-white shadow-md z-20 flex-shrink-0">
        <div class="px-4 py-2 sm:py-3 flex items-center justify-between gap-3">

          <div class="flex items-center gap-3 min-w-0">
            <div class="w-8 h-8 sm:w-9 sm:h-9 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
              </svg>
            </div>
            <div class="flex flex-col truncate">
              <h2 class="text-base sm:text-lg font-bold leading-none truncate">Sistema de Alarmas</h2>
              <p class="text-red-100 text-xs truncate hidden sm:block opacity-90 mt-0.5">Gestión de alertas en tiempo real</p>
            </div>
          </div>

          <div class="flex items-center gap-3 flex-shrink-0">
            <!-- Estadísticas -->
            <div class="flex items-center bg-black/20 rounded-lg p-1 gap-1">
              <div class="flex items-center px-2 py-1 rounded-md bg-red-500/90 hover:bg-red-500 transition-colors cursor-help" title="Alarmas Críticas">
                <span class="w-2 h-2 bg-white rounded-full animate-pulse mr-1.5"></span>
                <span class="text-sm font-bold leading-none">{{ estadisticasComputadas.criticas }}</span>
                <span class="text-[10px] ml-1.5 font-medium hidden md:inline leading-none">Críticas</span>
              </div>

              <div class="w-px h-3 bg-white/20 mx-0.5"></div>

              <div class="flex items-center px-2 py-1 rounded-md hover:bg-white/10 transition-colors cursor-help" title="Advertencias">
                <span class="w-2 h-2 bg-yellow-400 rounded-full mr-1.5"></span>
                <span class="text-sm font-bold leading-none">{{ estadisticasComputadas.advertencias }}</span>
                <span class="text-[10px] ml-1.5 font-medium hidden md:inline leading-none text-red-50">Alertas</span>
              </div>

              <div class="w-px h-3 bg-white/20 mx-0.5"></div>

              <div class="flex items-center px-2 py-1 rounded-md hover:bg-white/10 transition-colors cursor-help" title="Información">
                <span class="w-2 h-2 bg-blue-400 rounded-full mr-1.5"></span>
                <span class="text-sm font-bold leading-none">{{ estadisticasComputadas.informacion }}</span>
                <span class="text-[10px] ml-1.5 font-medium hidden md:inline leading-none text-red-50">Info</span>
              </div>
            </div>

            <button @click="$emit('close')" class="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-white/50 ml-1">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- FILTROS -->
      <div class="bg-gray-50 border-b border-gray-200 p-3 sm:p-4 flex-shrink-0 z-10">
        <div class="grid grid-cols-1 sm:grid-cols-12 gap-3">

          <!-- Búsqueda -->
          <div class="relative sm:col-span-12 lg:col-span-4">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
            <input v-model="filtros.busqueda" type="text" placeholder="Buscar por título o descripción..." class="block w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-red-500 focus:border-red-500 sm:text-sm transition duration-150 ease-in-out">
            <button v-if="filtros.busqueda" @click="filtros.busqueda = ''" class="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-400 hover:text-gray-600">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <div class="sm:col-span-8 lg:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-2">
            <!-- FILTRO PRIORIDAD -->
            <div class="relative">
              <button @click="toggleDropdown('prioridad')" class="w-full bg-white border border-gray-300 rounded-lg py-2 px-3 flex items-center justify-between text-sm shadow-sm hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-red-500">
                <span class="block truncate text-gray-700">
                  {{ obtenerTextoPrioridad(filtros.prioridad) }}
                </span>
                <svg :class="['h-4 w-4 text-gray-400 transition-transform flex-shrink-0', dropdownOpen.prioridad ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              <div v-if="dropdownOpen.prioridad" class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto sm:text-sm">
                <div @click="selectFiltro('prioridad', '')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 text-gray-900">Todas</div>
                <div @click="selectFiltro('prioridad', 'critica')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-red-50 text-red-700">Crítica</div>
                <div @click="selectFiltro('prioridad', 'alta')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-red-50 text-red-600">Alta</div>
                <div @click="selectFiltro('prioridad', 'media')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-yellow-50 text-yellow-700">Media</div>
                <div @click="selectFiltro('prioridad', 'baja')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 text-blue-700">Baja</div>
              </div>
            </div>

            <!-- FILTRO ESTADO -->
            <div class="relative">
              <button @click="toggleDropdown('estado')" class="w-full bg-white border border-gray-300 rounded-lg py-2 px-3 flex items-center justify-between text-sm shadow-sm hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-red-500">
                <span class="block truncate text-gray-700">
                  {{ obtenerTextoEstado(filtros.estado) }}
                </span>
                <svg :class="['h-4 w-4 text-gray-400 transition-transform flex-shrink-0', dropdownOpen.estado ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              <div v-if="dropdownOpen.estado" class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto sm:text-sm">
                <div @click="selectFiltro('estado', '')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 text-gray-900">Todos</div>
                <div @click="selectFiltro('estado', 'pendiente')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 text-gray-900">Pendiente</div>
                <div @click="selectFiltro('estado', 'en_proceso')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 text-gray-900">En Proceso</div>
                <div @click="selectFiltro('estado', 'resuelto')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 text-gray-900">Resuelto</div>
              </div>
            </div>

            <!-- FILTRO TIPO -->
            <div class="relative col-span-2 sm:col-span-1">
              <button @click="toggleDropdown('tipo')" class="w-full bg-white border border-gray-300 rounded-lg py-2 px-3 flex items-center justify-between text-sm shadow-sm hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-red-500">
                <span class="block truncate text-gray-700">
                  {{ obtenerTextoTipo(filtros.tipo) }}
                </span>
                <svg :class="['h-4 w-4 text-gray-400 transition-transform flex-shrink-0', dropdownOpen.tipo ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              <div v-if="dropdownOpen.tipo" class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto sm:text-sm">
                <div @click="selectFiltro('tipo', '')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 text-gray-900">Todos</div>
                <div @click="selectFiltro('tipo', 'error')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 text-gray-900">Error</div>
                <div @click="selectFiltro('tipo', 'advertencia')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 text-gray-900">Advertencia</div>
                <div @click="selectFiltro('tipo', 'informacion')" class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 text-gray-900">Información</div>
              </div>
            </div>
          </div>

          <div class="sm:col-span-4 lg:col-span-2 flex items-center justify-end space-x-2">
            <button v-if="hayFiltrosActivos" @click="limpiarFiltros" class="px-3 py-2 text-xs sm:text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors font-medium whitespace-nowrap">
              Limpiar
            </button>

            <button @click="cargarAlarmas" :disabled="isLoading" class="w-full sm:w-auto flex items-center justify-center space-x-2 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-all disabled:opacity-50">
              <svg :class="['w-4 h-4 text-gray-500', isLoading ? 'animate-spin' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              <span>{{ isLoading ? '...' : 'Actualizar' }}</span>
            </button>
          </div>
        </div>

        <div class="mt-2 text-[10px] sm:text-xs text-gray-500 flex justify-between items-center px-1">
          <span>{{ alarmasFiltradas.length }} alarma{{ alarmasFiltradas.length !== 1 ? 's' : '' }} en vista</span>
          <span v-if="todasLasAlarmas.length > 0">Total sistema: {{ todasLasAlarmas.length }}</span>
        </div>
      </div>

      <!-- CONTENIDO -->
      <div class="flex-1 overflow-y-auto bg-gray-50 p-4" id="alarmas-container">

        <!-- Error -->
        <div v-if="errorMessage" class="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg shadow-sm">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3 flex-1 md:flex md:justify-between">
              <p class="text-sm text-red-700">{{ errorMessage }}</p>
              <p class="mt-2 text-sm md:mt-0 md:ml-6">
                <button @click="errorMessage = null" class="whitespace-nowrap font-medium text-red-700 hover:text-red-600 transition-colors">
                  Descartar
                </button>
              </p>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="grid grid-cols-1 gap-4">
          <div v-for="n in 5" :key="n" class="bg-white rounded-xl border border-gray-100 p-4 shadow-sm animate-pulse">
            <div class="flex space-x-4">
              <div class="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0"></div>
              <div class="flex-1 space-y-3">
                <div class="flex justify-between">
                  <div class="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div class="h-4 bg-gray-200 rounded w-16"></div>
                </div>
                <div class="h-3 bg-gray-200 rounded w-3/4"></div>
                <div class="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sin resultados -->
        <div v-else-if="alarmasFiltradas.length === 0" class="flex flex-col items-center justify-center py-20 text-center h-full">
          <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900">Todo está tranquilo</h3>
          <p class="text-gray-500 mt-1 max-w-sm px-4">
            {{ hayFiltrosActivos ? 'No se encontraron alarmas con los filtros actuales.' : 'No hay alarmas activas en este momento.' }}
          </p>
          <button v-if="hayFiltrosActivos" @click="limpiarFiltros" class="mt-6 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 shadow-sm transition-all">
            Limpiar filtros de búsqueda
          </button>
        </div>

        <!-- Lista de alarmas -->
        <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-10">
          <div v-for="alarma in alarmasFiltradas" :key="alarma.id" class="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden group flex flex-col">
            <div class="p-4 sm:p-5 flex-1">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 pt-1">
                  <div :class="['w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-sm ring-2 ring-offset-2 ring-transparent group-hover:ring-gray-100 transition-all', obtenerColorPrioridad(alarma.prioridad)]">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                    </svg>
                  </div>
                </div>

                <div class="flex-1 min-w-0">
                  <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h3 class="text-base font-bold text-gray-900 leading-snug">
                      {{ alarma.titulo }}
                    </h3>

                    <div class="flex items-center gap-2 flex-wrap sm:flex-nowrap flex-shrink-0">
                      <span class="text-xs text-gray-400 flex items-center bg-gray-50 px-2 py-0.5 rounded border border-gray-100 whitespace-nowrap">
                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        {{ formatearFecha(alarma.fecha_creacion) }}
                      </span>
                      <span :class="['inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide whitespace-nowrap', obtenerClaseEstado(alarma.estado)]">
                        {{ obtenerTextoEstado(alarma.estado) }}
                      </span>
                    </div>
                  </div>

                  <p class="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">
                    {{ alarma.descripcion }}
                  </p>

                  <div class="flex items-center gap-x-4 gap-y-2 flex-wrap text-xs text-gray-500">
                    <div v-if="alarma.dispositivo" class="flex items-center" title="Dispositivo">
                      <svg class="w-3.5 h-3.5 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path>
                      </svg>
                      <span class="font-medium truncate max-w-[150px]">{{ alarma.dispositivo.nombre }}</span>
                    </div>
                    <div class="flex items-center capitalize" title="Tipo de alarma">
                      <svg class="w-3.5 h-3.5 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                      </svg>
                      {{ obtenerTextoTipo(alarma.tipo) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="bg-gray-50/80 px-4 py-3 border-t border-gray-100 flex items-center justify-between mt-auto">
              <button @click="toggleDetalles(alarma.id)" class="text-xs sm:text-sm text-gray-600 hover:text-red-700 font-medium focus:outline-none transition-colors flex items-center">
                <span>{{ alarma.mostrarDetalles ? 'Ocultar detalles' : 'Ver detalles' }}</span>
                <svg :class="['w-3 h-3 ml-1 transition-transform', alarma.mostrarDetalles ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              <button v-if="alarma.estado !== 'resuelto'" @click="marcarComoResuelta(alarma.id)" class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all active:scale-95">
                <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Resolver
              </button>
              <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-400 cursor-default border border-gray-200">
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Resuelta
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AlarmasService from '@/services/dispositivos/alarmas.js'

export default {
  name: 'AlarmasView',

  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close'],

  data() {
    return {
      isLoading: false,
      errorMessage: null,

      dropdownOpen: {
        prioridad: false,
        estado: false,
        tipo: false
      },

      // ⭐ FILTROS (solo frontend)
      filtros: {
        prioridad: '',
        estado: '',
        tipo: '',
        busqueda: ''
      },

      // ⭐ TODAS las alarmas sin filtrar
      todasLasAlarmas: []
    }
  },

  computed: {
    /**
     * ✅ FILTRADO EN COMPUTED (como ManageAccount)
     */
    alarmasFiltradas() {
      let result = [...this.todasLasAlarmas]

      // Filtro de búsqueda
      if (this.filtros.busqueda) {
        const search = this.filtros.busqueda.toLowerCase()
        result = result.filter(alarma =>
          (alarma.titulo && alarma.titulo.toLowerCase().includes(search)) ||
          (alarma.descripcion && alarma.descripcion.toLowerCase().includes(search))
        )
      }

      // Filtro por prioridad
      if (this.filtros.prioridad) {
        result = result.filter(alarma => alarma.prioridad === this.filtros.prioridad)
      }

      // Filtro por estado
      if (this.filtros.estado) {
        result = result.filter(alarma => alarma.estado === this.filtros.estado)
      }

      // Filtro por tipo
      if (this.filtros.tipo) {
        result = result.filter(alarma => alarma.tipo === this.filtros.tipo)
      }

      return result
    },

    /**
     * ✅ ESTADÍSTICAS COMPUTADAS (de alarmas filtradas)
     */
    estadisticasComputadas() {
      return {
        criticas: this.todasLasAlarmas.filter(a => a.prioridad === 'critica').length,
        advertencias: this.todasLasAlarmas.filter(a => a.prioridad === 'alta').length,
        informacion: this.todasLasAlarmas.filter(a => a.prioridad === 'baja' || a.prioridad === 'media').length
      }
    },

    hayFiltrosActivos() {
      return !!(this.filtros.prioridad || this.filtros.estado || this.filtros.tipo || this.filtros.busqueda)
    }
  },

  methods: {
    async cargarAlarmas() {
      try {
        this.isLoading = true
        this.errorMessage = null

        console.log('🔄 Cargando TODAS las alarmas...')

        // ⭐ CARGAR TODAS (sin filtros en backend)
        const alarmas = await AlarmasService.obtenerTodasLasAlarmas()

        this.todasLasAlarmas = alarmas

        console.log(`✅ ${alarmas.length} alarmas cargadas`)
        console.log('🔍 FILTRADO SE HARÁ EN COMPUTED')

      } catch (error) {
        console.error('❌ Error cargando alarmas:', error)
        this.errorMessage = error.message || 'Error cargando alarmas'
      } finally {
        this.isLoading = false
      }
    },

    async marcarComoResuelta(alarmaId) {
      try {
        await AlarmasService.actualizarEstadoAlarma(alarmaId, 'resuelto')

        // ⭐ ACTUALIZAR EN ARRAY LOCAL
        const alarma = this.todasLasAlarmas.find(a => a.id === alarmaId)
        if (alarma) {
          alarma.estado = 'resuelto'
        }

      } catch (error) {
        console.error('❌ Error resolviendo alarma:', error)
        this.errorMessage = 'Error al resolver la alarma'
      }
    },

    toggleDetalles(alarmaId) {
      const alarma = this.todasLasAlarmas.find(a => a.id === alarmaId)
      if (alarma) {
        alarma.mostrarDetalles = !alarma.mostrarDetalles
      }
    },

    limpiarFiltros() {
      this.filtros = {
        prioridad: '',
        estado: '',
        tipo: '',
        busqueda: ''
      }
    },

    selectFiltro(tipo, valor) {
      console.log(`🎯 Seleccionado: ${tipo} = "${valor}"`)
      this.filtros[tipo] = valor
      this.dropdownOpen[tipo] = false
    },

    toggleDropdown(tipo) {
      Object.keys(this.dropdownOpen).forEach(key => {
        if (key !== tipo) {
          this.dropdownOpen[key] = false
        }
      })
      this.dropdownOpen[tipo] = !this.dropdownOpen[tipo]
    },

    closeAllDropdowns() {
      Object.keys(this.dropdownOpen).forEach(key => {
        this.dropdownOpen[key] = false
      })
    },

    handleClickOutside(event) {
      const dropdownElements = document.querySelectorAll('.relative')
      let clickedOutside = true

      dropdownElements.forEach(element => {
        if (element.contains(event.target)) {
          clickedOutside = false
        }
      })

      if (clickedOutside) {
        this.closeAllDropdowns()
      }
    },

    // ⭐ MÉTODOS DE TEXTO/CLASE
    obtenerTextoPrioridad(prioridad) {
      const map = {
        '': 'Prioridad',
        'critica': 'Crítica',
        'alta': 'Alta',
        'media': 'Media',
        'baja': 'Baja'
      }
      return map[prioridad] || 'Prioridad'
    },

    obtenerTextoEstado(estado) {
      const map = {
        '': 'Estado',
        'pendiente': 'Pendiente',
        'en_proceso': 'En Proceso',
        'resuelto': 'Resuelto'
      }
      return map[estado] || 'Estado'
    },

    obtenerTextoTipo(tipo) {
      const map = {
        '': 'Tipo',
        'error': 'Error',
        'advertencia': 'Advertencia',
        'informacion': 'Información'
      }
      return map[tipo] || 'Tipo'
    },

    obtenerColorPrioridad(prioridad) {
      const colores = {
        'critica': 'bg-red-600',
        'alta': 'bg-red-500',
        'media': 'bg-yellow-500',
        'baja': 'bg-blue-500'
      }
      return colores[prioridad] || 'bg-gray-500'
    },

    obtenerClaseEstado(estado) {
      const clases = {
        'pendiente': 'bg-red-100 text-red-800',
        'en_proceso': 'bg-orange-100 text-orange-800',
        'resuelto': 'bg-green-100 text-green-800'
      }
      return clases[estado] || 'bg-gray-100 text-gray-800'
    },

    formatearFecha(fecha) {
      try {
        const date = new Date(fecha)
        return date.toLocaleDateString('es-CL', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return 'Fecha inválida'
      }
    }
  },

  watch: {
    isOpen: {
      handler(newValue) {
        if (newValue) {
          this.cargarAlarmas()
          document.addEventListener('click', this.handleClickOutside)
        } else {
          document.removeEventListener('click', this.handleClickOutside)
          this.closeAllDropdowns()
        }
      },
      immediate: true
    }
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style scoped>
#alarmas-container::-webkit-scrollbar {
  width: 8px;
}

#alarmas-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

#alarmas-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

#alarmas-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>